import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarPlus, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Badge, de, feld } from "@/components/qm/shared";
import { MassnahmenZulassung } from "@/components/MassnahmenZulassung";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fehler = (e) => toast.error(e?.response?.data?.detail || "Fehler");
const LEER = { vorlage_id: "", kurs_name: "", start: "", ende: "", max_plaetze: 16, unterrichtszeit: "09:00–15:00", form: "Online", dozentinnen: "" };

const KalenderAbo = () => {
  const [link, setLink] = useState("");
  const laden = () => axios.get(`${API}/kalender-abo/mein`).then((r) => setLink(r.data.link)).catch(fehler);
  const kopieren = () => navigator.clipboard?.writeText(link).then(() => toast.success("Link kopiert")).catch(() => toast.message("Bitte Link von Hand kopieren"));
  const neu = () => window.confirm("Neuen Link erzeugen? Der alte Link funktioniert dann nicht mehr.") && axios.post(`${API}/kalender-abo/mein/neu`).then(laden).catch(fehler);
  return (
    <div className="bg-white rounded-xl border border-[#F0E6DC] p-4 text-sm" data-testid="kalender-abo">
      <p className="font-semibold flex items-center gap-2"><CalendarPlus size={16} className="text-[#9A3B2E]" />Mein Kalender-Abo</p>
      <p className="text-xs text-[#8E7B71] mt-1">Für Handy/Outlook/Google: zeigt deine Kurstermine und Unterrichtstage – ohne Teilnehmernamen.</p>
      {link ? (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="break-all select-all text-xs bg-[#F5F0EB] rounded px-2 py-1" data-testid="kalender-abo-link">{link}</span>
          <Button size="sm" variant="outline" onClick={kopieren} data-testid="kalender-abo-kopieren"><Copy size={14} className="mr-1" />Kopieren</Button>
          <Button size="sm" variant="ghost" onClick={neu} data-testid="kalender-abo-neu">Neuen Link</Button>
        </div>
      ) : <Button size="sm" className="mt-2" variant="outline" onClick={laden} data-testid="kalender-abo-anzeigen">Abo-Link anzeigen</Button>}
    </div>
  );
};

const Neu = ({ vorlagen, onFertig }) => {
  const [t, setT] = useState(LEER);
  const [ver, setVer] = useState([]);
  useEffect(() => { if (t.vorlage_id) axios.get(`${API}/kurstermine/vorlagen/${t.vorlage_id}/versionen`).then((r) => setVer(r.data)).catch(() => setVer([])); else setVer([]); }, [t.vorlage_id]);
  const s = (k) => (e) => setT({ ...t, [k]: e.target.value });
  const senden = (e) => {
    e.preventDefault();
    axios.post(`${API}/kurstermine`, { ...t, dozentinnen: t.dozentinnen.split(",") })
      .then((r) => { toast.success(`Angelegt bis ${de(r.data.ende)} – KURSNET-Aufgabe erstellt`); setT(LEER); onFertig(); }).catch(fehler);
  };
  return (
    <form onSubmit={senden} className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-white rounded-xl p-4 border border-[#F0E6DC]" data-testid="kurstermin-form">
      <select className={`${feld} col-span-2`} value={t.vorlage_id} onChange={s("vorlage_id")} data-testid="kurstermin-vorlage">
        <option value="">Vorlage wählen (Ende wird berechnet) …</option>
        {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.tage_count} Tage</option>)}
      </select>
      <input className={feld} placeholder="Kursname (ohne Vorlage)" value={t.kurs_name} onChange={s("kurs_name")} data-testid="kurstermin-name" />
      <label className="text-xs text-[#8E7B71]">Plätze<input className={feld} type="number" min={1} value={t.max_plaetze} onChange={s("max_plaetze")} data-testid="kurstermin-plaetze" /></label>
      <label className="text-xs text-[#8E7B71]">Start<input className={feld} type="date" required value={t.start} onChange={s("start")} data-testid="kurstermin-start" /></label>
      <label className="text-xs text-[#8E7B71]">Ende (leer = aus Vorlage)<input className={feld} type="date" value={t.ende} onChange={s("ende")} data-testid="kurstermin-ende" /></label>
      <input className={`${feld} self-end`} placeholder="Unterrichtszeit" value={t.unterrichtszeit} onChange={s("unterrichtszeit")} data-testid="kurstermin-zeit" />
      <select className={`${feld} self-end`} value={t.form} onChange={s("form")} data-testid="kurstermin-form-art">{["Online", "Präsenz", "Blended"].map((x) => <option key={x}>{x}</option>)}</select>
      <input className={`${feld} col-span-2 md:col-span-3`} placeholder="Dozent/innen (E-Mail, mehrere mit Komma)" value={t.dozentinnen} onChange={s("dozentinnen")} data-testid="kurstermin-dozentinnen" />
      <Button type="submit" size="sm" className="bg-[#9A3B2E] hover:bg-[#7F2F25]" data-testid="kurstermin-anlegen">Anlegen</Button>
      {ver.length > 0 && <p className="col-span-2 md:col-span-4 text-xs text-[#8E7B71]" data-testid="vorlage-versionen">
        Vorlage Version {ver[0].source || "–"} ({ver[0].tage_count} Tage, {ver[0].ue_count} UE){ver.length > 1 && ` · ${ver.length - 1} frühere Version(en) gesichert, zuletzt ${de(ver[1].backed_up_at)}`}</p>}
    </form>
  );
};

const Zeile = ({ t, buero, laden }) => {
  const pct = Math.min(100, Math.round((t.belegt / Math.max(1, t.max_plaetze)) * 100));
  const aendern = () => {
    const max_plaetze = window.prompt("Plätze:", t.max_plaetze); if (max_plaetze === null) return;
    const start = window.prompt("Start (JJJJ-MM-TT):", t.start); if (start === null) return;
    const ende = window.prompt("Ende (JJJJ-MM-TT):", t.ende); if (ende === null) return;
    axios.put(`${API}/kurstermine/${t.id}`, { max_plaetze, start, ende }).then(() => { toast.success("Gespeichert"); laden(); }).catch(fehler);
  };
  const kursnet = () => axios.post(`${API}/kurstermine/${t.id}/kursnet-erledigt`).then(() => { toast.success("KURSNET als aktuell markiert"); laden(); }).catch(fehler);
  return (
    <div className="p-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid={`kurstermin-${t.id}`}>
      <div className="min-w-[240px]"><p className="font-medium">{t.kurs_name}</p><p className="text-xs text-[#78716c]">{de(t.start)} – {de(t.ende)} · {t.unterrichtszeit} · {t.form}</p></div>
      <div className="w-44" data-testid={`kurstermin-belegung-${t.id}`}>
        <div className="h-2 rounded-full bg-[#F5F0EB] overflow-hidden"><div className={`h-full ${t.ueberbucht ? "bg-[#C2452F]" : "bg-[#4F6650]"}`} style={{ width: `${pct}%` }} /></div>
        <p className="text-xs mt-1">{t.belegt} von {t.max_plaetze} belegt · <b>{t.frei} frei</b>{t.ueberbucht && " · überbucht"}</p>
      </div>
      <span className="text-xs text-[#78716c]">{(t.dozentinnen || []).join(", ") || "keine Dozentin"}</span>
      <span className="ml-auto flex items-center gap-2">
        <Badge ton={t.kursnet_status === "offen" ? "gelb" : "gruen"} testid={`kurstermin-kursnet-${t.id}`}>KURSNET {t.kursnet_status === "offen" ? "aktualisieren" : "aktuell"}</Badge>
        {buero && t.kursnet_status === "offen" && <Button size="sm" variant="outline" onClick={kursnet} data-testid={`kurstermin-kursnet-ok-${t.id}`}>Erledigt</Button>}
        {buero && <Button size="sm" variant="ghost" onClick={aendern} data-testid={`kurstermin-aendern-${t.id}`}>Ändern</Button>}
      </span>
    </div>
  );
};

export default function Kurstermine() {
  const { user } = useAuth();
  const buero = ["gf", "verwaltung"].includes(user?.role);
  const [liste, setListe] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const laden = useCallback(() => axios.get(`${API}/kurstermine`).then((r) => setListe(r.data)).catch(fehler), []);
  useEffect(() => { laden(); if (buero) axios.get(`${API}/kurstermine/vorlagen`).then((r) => setVorlagen(r.data)).catch(() => {}); }, [laden, buero]);
  return (
    <div className="p-6 lg:p-8 max-w-7xl space-y-4" data-testid="kurstermine-seite">
      <div><p className="text-[11px] uppercase tracking-[0.2em] text-[#9A3B2E] font-semibold">Maßnahmen &amp; Kurse</p><h1 className="text-3xl font-semibold mt-1">Kurstermine &amp; Belegung</h1></div>
      {buero && <Neu vorlagen={vorlagen} onFertig={laden} />}
      <div className="bg-white rounded-xl border border-[#F0E6DC] divide-y divide-[#F6E3DA]">
        {liste.length === 0 && <p className="p-4 text-sm text-[#8E7B71]" data-testid="kurstermine-leer">Keine laufenden oder kommenden Kurstermine.</p>}
        {liste.map((t) => <Zeile key={t.id} t={t} buero={buero} laden={laden} />)}
      </div>
      <KalenderAbo />
      {buero && <MassnahmenZulassung />}
    </div>
  );
}
